import { Button, cn, TeamChannelDialog } from "@tellescope/ui";
import { useNavbarShallowStore } from "../../store";
import { useState } from "react";
import { Camera, Phone } from "lucide-react";
import { ROUTES_CONFIG, useActiveTakeoverCard } from "../../routing";
import { useAuth } from "@tellescope/tanstack-query";
import { useNavigate } from "react-router-dom";
import { DialerWindow } from "../DialerPanel";
import { SidebarAccountButton } from "../AccountDialog";
import { NavbarNavItem } from "./NavbarNavItem";
import { PAGE_HEADER_SIZE_STRING } from "../../constants/header-sizing";
import { NAVBAR_WIDTH_CLASSES } from "../../constants/navbar-sizing";
import { useCallingShallowStore } from "../../store/calling-store";
import { useTranslation } from "react-i18next";
import { useAppPermissionQuery, useOrgFeatures } from "../../permissions";

export const AppNavbar = () => {
  const { t } = useTranslation("navigation");
  const navigate = useNavigate();
  const { session, selectedOrganization, currentUser, isAuthenticated, isInitializing, isAdmin } = useAuth();
  const { isFeatureEnabled } = useOrgFeatures();
  const { isCollapsed } = useNavbarShallowStore("isCollapsed");
  const takeoverCard = useActiveTakeoverCard();
  const isSessionInitialized = isAuthenticated && !isInitializing && !!currentUser && !!selectedOrganization;
  const [isTeamChannelDialogOpen, setIsTeamChannelDialogOpen] = useState(false);
  const { isDialerOpen, dialerEnduser, dialerMode, openDialer, closeDialer } = useCallingShallowStore(
    "isDialerOpen",
    "dialerEnduser",
    "dialerMode",
    "openDialer",
    "closeDialer"
  );

  // Check if user has Twilio calling access
  const hasTwilioAccess = !!(session?.userInfo?.twilioNumber || session?.userInfo?.orgTwilioNumber);
  const {
    create: { access: canVideoCall },
  } = useAppPermissionQuery({
    model: "calendar_events",
    actions: ["create"],
  });
  const hasCallingAccess = isFeatureEnabled("call");

  return (
    <>
      <div
        className={cn(
          "flex flex-col gap-0 flex-shrink-0 h-screen bg-sidebar text-sidebar-foreground border-r transition-all duration-300 z-50 overflow-hidden",
          isCollapsed ? NAVBAR_WIDTH_CLASSES.collapsed : NAVBAR_WIDTH_CLASSES.expanded
        )}
      >
        <div className="flex-grow flex flex-col overflow-hidden">
          <div
            className={`px-2 border-b flex items-center flex-shrink`}
            style={{
              height: PAGE_HEADER_SIZE_STRING,
              minHeight: PAGE_HEADER_SIZE_STRING,
            }}
          >
            <SidebarAccountButton />
          </div>
          {takeoverCard}
          <div className="flex flex-col gap-0 px-2 pt-2 flex-grow overflow-hidden">
            {Object.entries(ROUTES_CONFIG).map(([path, config]) => {
              if ("shouldShow" in config) {
                if (typeof config.shouldShow === "function") {
                  if (!isSessionInitialized) return null;
                  if (!config.shouldShow({ session, selectedOrganization, currentUser, isAdmin })) return null;
                } else if (!config.shouldShow) {
                  return null;
                }
              }

              if ("adminOnly" in config && config.adminOnly && !isAdmin) return null;

              if ("modelForAccess" in config && config.modelForAccess && typeof config.modelForAccess === "string") {
                // Honor the role's sidebar configuration: only show a model-backed item when the role
                // grants read access and hasn't hidden it from the sidebar. Mirrors get-route-component.tsx.
                const access = session.userInfo.access[config.modelForAccess];
                if (access?.showInSidebar === false) return null;
                if (!access?.read) return null;
              }

              return (
                <NavbarNavItem key={`${path ?? ""}-${"label" in config ? config.label : ""}`} path={path} {...config} />
              );
            })}
          </div>
          <div className="w-full flex">
            <div className="w-full border-t border-t-gray-200" />
          </div>
        </div>

        <div className="p-2 flex-shrink grid grid-cols-2 gap-2 items-center">
          {hasTwilioAccess && hasCallingAccess && (
            <Button
              variant="outline"
              className="grow"
              onClick={() => openDialer()}
              data-trackingid="place-call-dialog-open"
            >
              <Phone className="h-4 w-4" />
              {!isCollapsed && <span className="text-sm">{t("Call")}</span>}
            </Button>
          )}
          {hasTwilioAccess && canVideoCall && (
            <Button
              variant="outline"
              className="grow"
              onClick={() => openDialer({ mode: "video" })}
              data-trackingid="place-video-call-dialog-open"
            >
              <Camera className="h-4 w-4" />
              {!isCollapsed && <span className="text-sm">{t("Video Call")}</span>}
            </Button>
          )}
        </div>
      </div>
      <TeamChannelDialog
        open={isTeamChannelDialogOpen}
        onOpenChange={setIsTeamChannelDialogOpen}
        defaultExpanded={false}
        showTrigger={false}
        availableUsers={[]} // TODO: [bb] move this inside the dialog
        onCreateChannel={({ id }) => {
          setIsTeamChannelDialogOpen(false);
          navigate(`/team-chat/${id}`);
        }}
      />
      {hasTwilioAccess && hasCallingAccess && (
        <DialerWindow
          open={isDialerOpen}
          onOpenChange={(open) => {
            if (!open) closeDialer();
          }}
          mode={dialerMode}
          initialEnduser={dialerEnduser ?? undefined}
        />
      )}
    </>
  );
};
